import Bugsnag from '@bugsnag/js'
import Cookies from 'js-cookie'
import getConfig from 'next/config'
import omitBy from 'lodash/omitBy'
import { Dispatch } from 'react'
import { FormikActions } from 'formik'
import { useStripe, useElements, CardCvcElement, CardElement } from '@stripe/react-stripe-js'

import ShippingAddressType from 'server/enums/ShippingAddressType'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import useCurrency from 'shared/hooks/useCurrency'
import { Address } from 'shared/types/Address'
import {
  IAction,
  ICreditCardSuccessAction,
  IPanelUpdateAction,
  IErrorAction,
} from 'checkout/containers/CheckoutStateReducer'
import PaymentType from 'shared/enums/PaymentType'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { Order } from 'shared/types/Order'
import { STORAGE } from 'shared/enums/SitePreferences'
import { addressErrorFieldMap } from 'shared/addressValidations'
import { isTimeoutError, timeoutMessage } from '../utils/timeout'
import { t } from 'localization'
import { useAuth } from 'shared/hooks/useAuth'
import { useCreateAddressMutation } from 'api/addressesApi'
import { useCreateBillingInfoMutation } from 'api/billingInfosApi'
import {
  useCreateAfterPayTokenMutation,
  useCreateKlarnaPaymentSessionMutation,
  useCreatePayPalPaymentSessionMutation,
  useUpdateOrderMutation,
  useVerifyBillingMutation,
} from 'api/ordersApi'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useUser } from 'shared/hooks/useUser'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'

const TP = 'checkout.hooks.usePaymentsRTK'

const canPayWithCredits = (order: Order) => {
  const creditsAvailable = order?.localizedCreditCents?.amount
  const finalPrice = order?.localizedFinalPriceCents?.amount
  return creditsAvailable && finalPrice && creditsAvailable >= finalPrice
}

type UsePaymentsProps = {
  dispatch: Dispatch<IAction | ICreditCardSuccessAction | IErrorAction | IPanelUpdateAction>
  order?: Partial<Order>
}

export const usePaymentsRTK = ({ dispatch, order }: UsePaymentsProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const { logoutUser } = useAuth()
  const { currentUser } = useUser()
  const { selectedCurrency, setCurrency } = useCurrency()
  const { setCurrencyCodeContext } = useShoppingRegionContext()
  const isExpressKlarnaEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_EXPRESS_KLARNA)

  const [createAddress] = useCreateAddressMutation()
  const [createAfterPayToken] = useCreateAfterPayTokenMutation()
  const [createKlarnaPaymentSession] = useCreateKlarnaPaymentSessionMutation()
  const [createPayPalPaymentSession] = useCreatePayPalPaymentSessionMutation()
  const [createBillingInfo] = useCreateBillingInfoMutation()
  const [updateOrder] = useUpdateOrderMutation()
  const [verifyBilling] = useVerifyBillingMutation()

  const fallbackErrorMessage = t(
    `${TP}.requestFailure`,
    'Something went wrong.  Please try again or reach out to support@flightclub.com',
  )

  const trackError = (methodName: string, error: Error) => {
    const notifyError = new Error(error?.data?.messages?.[0] || error?.message || methodName)
    Bugsnag.notify(notifyError, (event) => {
      event.addMetadata('checkout', {
        addressId: order?.addressId,
        billingInfoId: order?.billingInfoId,
        id: order?.id,
        isInstantShip: order?.isInstantShip,
        localizedCreditCents: order?.localizedCreditCents,
        localizedFinalPriceCents: order?.localizedFinalPriceCents,
        localizedPromoCodeValueCents: order?.localizedPromoCodeValueCents,
        localizedShippingCents: order?.localizedShippingCents,
        orderNumber: order?.number,
        paymentType: order?.paymentType,
        shippingAddressInRegion: order?.shippingAddressInRegion,
        shippingServiceLevel: order?.shippingServiceLevel,
        status: order?.status,
      })
      event.addMetadata('error', {
        dataMessages: error?.data?.messages,
        file: 'usePaymentsRTK',
        message: error?.message,
        method: methodName,
        status: error?.status,
      })
      event.addMetadata('product', {
        boxCondition: order?.product?.boxCondition,
        consigned: order?.product?.consigned,
        id: order?.product?.id,
        name: order?.product?.productTemplate?.name,
        saleStatus: order?.product?.saleStatus,
        shoeCondition: order?.product?.shoeCondition,
        size: order?.product?.size,
        slug: order?.product?.slug || order?.product?.productTemplate?.slug,
        variantShoeCondition: order?.product?.variantShoeCondition,
      })
      event.addMetadata('user', {
        id: currentUser?.id,
        isGuest: currentUser?.isGuest,
      })
      event.context = 'checkout'
      event.severity = 'error'
    })
  }

  const sendTrackingCvvSubmit = (errorMessage?: string) => {
    sendTrackingEvent('CHECKOUT_CVV_SUBMIT_TAP', {
      condition: order?.product?.shoeCondition,
      currency_code: selectedCurrency?.isoCode,
      instant_ship: order?.isInstantShip,
      is_apparel_product: order?.product?.productTemplate?.productCategory !== 'shoes',
      price: order?.product?.localizedPriceCents?.amount,
      product_template_slug: order?.product?.productTemplate?.slug,
      size: order?.product?.size,
      ...(errorMessage && { error_message: errorMessage }),
    })
  }

  const addNewCreditCardRTK = async (address: Address, formikActions: FormikActions<Address>) => {
    const cardElement = elements?.getElement(CardElement)
    try {
      if (!order?.number) {
        throw new Error(
          t(`${TP}.noOrderNumberError`, "No order number, can't create stripe payment"),
        )
      }
      if (!stripe || !cardElement) {
        throw new Error(t(`${TP}.noCardError`, 'Can not add new card. Stripe is unavailable'))
      }
      dispatch({ type: 'paymentMethodRequest' })
      const { token, error } = await stripe.createToken(cardElement, {
        name: address.name,
        address_line1: address.address1,
        address_line2: address.address2 || '',
        address_city: address.city,
        address_state: address.state,
        address_zip: address.postalCode,
        address_country: address.countryCode,
      })
      const stripeToken = token?.id
      if (error) {
        throw error
      }
      if (!stripeToken) {
        throw new Error(
          t(`${TP}.noOrderNumberError`, {
            defaultValue: "No stripe token, can't create stripe payment for {orderNumber}",
            orderNumber: order.number,
          }),
        )
      }

      // Create billing address
      const billingAddressBody = {
        address: omitBy(
          {
            ...address,
            addressType: ShippingAddressType.Billing,
          },
          (x) => x === '' || x === undefined || x === null,
        ),
      }
      const billingAddress = await createAddress(billingAddressBody).unwrap()

      // Create billing info
      const billingInfoBody = {
        billingInfo: {
          billingAddressId: billingAddress.id,
          name: address.name,
          stripeToken,
        },
      }
      const billingInfo = await createBillingInfo(billingInfoBody).unwrap()

      // Connect billing info to order
      const orderBody = {
        orderNumber: order.number,
        order: {
          billingInfoId: billingInfo.id,
        },
      }
      const updatedOrder = await updateOrder(orderBody).unwrap()
      sendTrackingEvent('SAVE_CARD_TAP', {
        order_number: order.number,
        billing_info_id: billingInfo.id,
      })
      dispatch({
        type: 'paymentMethodRequestSuccess',
        payload: updatedOrder,
      })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      if (addressErrorFieldMap().has(errorMessage)) {
        formikActions.setErrors({ [addressErrorFieldMap().get(errorMessage)!]: errorMessage })
        dispatch({
          type: 'paymentMethodRequestFailure',
          payload: t(`${TP}.paymentMethodRequestFailure`, 'Please update the fields below'),
        })
        return
      }
      if (/address/i.test(errorMessage)) {
        formikActions.setFieldError(
          'address1',
          t(`${TP}.addressFieldError`, 'Please update your address'),
        )
      }
      trackError('addNewCreditCardRTK', error)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const selectAffirmRTK = (processorName: string, shippingAddress: Address) => {
    const { publicRuntimeConfig } = getConfig()
    const orderNumber = order?.number
    const productTemplate = order?.product?.productTemplate
    if (!orderNumber || !productTemplate || !shippingAddress) {
      const error = t(
        `${TP}.selectAffirmError`,
        'No order number present when user selects affirm, in selectAffirm, paymentMethodService',
      )
      throw new Error(error)
    }
    if (canPayWithCredits(order)) {
      // we shouldn't use affirm if the order has all credits
      return null
    }
    dispatch({ type: 'paymentMethodRequest' })
    // Create Affirm session and open Affirm
    const urlOrigin = window.location.origin
    window.affirm.checkout({
      config: {
        financial_product_key: publicRuntimeConfig.affirmFinancialKey,
      },
      merchant: {
        user_cancel_url: `${urlOrigin}/checkout/${orderNumber}?status=CANCELLED`,
        user_confirmation_url: `${urlOrigin}/checkout/${orderNumber}/order-with-affirm?processorName=${processorName}`,
        user_confirmation_url_action: 'GET',
      },
      shipping: {
        name: {
          full: shippingAddress.name,
        },
        address: {
          line1: shippingAddress.address1,
          line2: shippingAddress.address2 || '',
          city: shippingAddress.city,
          state: shippingAddress.state,
          zipcode: shippingAddress.postalCode,
        },
      },
      items: [
        {
          display_name: productTemplate.name,
          sku: productTemplate.sku,
          unit_price: order?.localizedPriceCents?.amount,
          qty: 1,
          item_image_url: productTemplate.pictureUrl,
          item_url: `${urlOrigin}/sneakers/${order?.product?.productTemplate?.slug}`,
        },
      ],
      currency: 'USD',
      tax_amount: order?.localizedTaxCents?.amount,
      shipping_amount: order?.localizedShippingCents?.amount,
      total: order?.localizedFinalPriceCents?.amount,
    })
    window.affirm.checkout.open()
  }

  const selectAfterPayRTK = async () => {
    if (!order?.number || !order?.localizedFinalPriceCents?.amount) {
      const error = t(
        `${TP}.selectAfterPayError`,
        'No order number present when user selects AfterPay, in select payment method',
      )
      throw new Error(error)
    }
    try {
      dispatch({ type: 'paymentMethodRequest' })
      // Create AfterPay session and open AfterPay
      window.AfterPay.initialize({ countryCode: 'US' })
      const afterPayTokenBody = {
        orderNumber: order?.number,
        finalPriceCents: order?.localizedFinalPriceCents?.amount,
        redirectConfirmUrl: `${location.origin}/checkout/${order?.number}/order-with-afterpay`,
        redirectCancelUrl: `${location.origin}/checkout/${order?.number}`,
      }
      const data = await createAfterPayToken(afterPayTokenBody).unwrap()
      const token = data?.afterpayOrderToken
      window.AfterPay.redirect({ token })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      trackError('selectAfterPayRTK', error)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const selectCreditCardRTK = async (creditCardId: number) => {
    if (creditCardId === order?.billingInfoId) {
      return dispatch({
        type: 'paymentMethodRequestSuccess',
        payload: order,
      })
    }
    try {
      dispatch({ type: 'paymentMethodRequest' })
      // Connect selected billing info to order
      const orderBody = {
        orderNumber: order.number,
        order: {
          billingInfoId: creditCardId,
        },
      }
      const updatedOrder = await updateOrder(orderBody).unwrap()
      dispatch({
        type: 'paymentMethodRequestSuccess',
        payload: updatedOrder,
      })
    } catch (error) {
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      trackError('selectCreditCardRTK', error)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const selectKlarnaRTK = async () => {
    try {
      // Backend sets klarna_session_id on billing info. Before Klarna session begins,
      // billing info needs to be created and connected to the order
      dispatch({ type: 'paymentMethodRequest' })
      // Create billing info
      const billingInfoBody = {
        billingInfo: {
          email: currentUser?.email,
          paymentType: PaymentType.Klarna,
        },
      }
      const billingInfo = await createBillingInfo(billingInfoBody).unwrap()

      // Connect billing info to order
      const orderBody = {
        orderNumber: order?.number,
        order: {
          billingInfoId: billingInfo.id,
        },
      }
      await updateOrder(orderBody).unwrap()

      // Create Klarna session and open Klarna
      const urlOrigin = window.location.origin
      const klarnaSuccessUrl = isExpressKlarnaEnabled
        ? `${origin}/checkout/${order?.number}/order-with-klarna`
        : `${origin}/checkout/${order?.number}?klarna=success`
      const klarnaPaymentSessionBody = {
        country_code: 'us',
        merchant_urls: {
          success: klarnaSuccessUrl,
          cancel: `${urlOrigin}/checkout/${order?.number}?klarna=cancel`,
          back: `${urlOrigin}/checkout/${order?.number}?klarna=back`,
          failure: `${urlOrigin}/checkout/${order?.number}?klarna=failure`,
          error: `${urlOrigin}/checkout/${order?.number}?klarna=error`,
        },
        orderNumber: order?.number,
      }
      const klarnaPaymentSessionData = await createKlarnaPaymentSession(
        klarnaPaymentSessionBody,
      ).unwrap()
      window.location.href = klarnaPaymentSessionData?.redirectUrl
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      trackError('selectKlarnaRTK', error)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const selectPayPalRTK = async () => {
    try {
      // PayPal v2 Billing Info must be connected to order to create PayPal session
      dispatch({ type: 'paymentMethodRequest' })
      // Create billing info
      const billingInfoBody = {
        billingInfo: {
          email: currentUser?.email,
          paymentType: PaymentType.PayPalv2,
        },
      }
      const billingInfo = await createBillingInfo(billingInfoBody).unwrap()

      // Connect billing info to order
      const orderBody = {
        orderNumber: order?.number,
        order: {
          billingInfoId: billingInfo.id,
        },
      }
      await updateOrder(orderBody).unwrap()

      // Create PayPal session and open PayPal
      const urlOrigin = window.location.origin
      const redirectCancelUrl = `${urlOrigin}/checkout/${order?.number}?paypal=cancel`
      const redirectConfirmUrl = `${urlOrigin}/checkout/${order?.number}/order-with-paypal`
      const payPalPaymentSessionBody = {
        orderNumber: order?.number,
        redirectCancelUrl,
        redirectConfirmUrl,
      }
      const payPalPaymentSessionData = await createPayPalPaymentSession(
        payPalPaymentSessionBody,
      ).unwrap()
      window.location.href = payPalPaymentSessionData?.paypalCheckoutLink
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      trackError('selectPayPalRTK', error)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const updateOrderCurrencyRTK = async () => {
    dispatch({ type: 'orderRequest' })
    try {
      const expires = new Date().getFullYear() + 1
      // Set currencyCode cookie to USD
      Cookies.set(STORAGE.CURRENCYCODE, 'USD', { expires })
      // Set currencyOverwritten cookie to prevent regionPreferenceMiddleware from overwriting currency cookies
      Cookies.set(STORAGE.CURRENCY_OVERWRITTEN, 'true', { expires })
      // Set currency cookie to full currency object { isoCode: USD, ... }
      await setCurrency('USD')
      // Set currency to USD throughout app context
      await setCurrencyCodeContext('USD')
      const orderBody = {
        orderNumber: order?.number,
        order: { updateCurrency: true },
      }
      const updatedOrder = await updateOrder(orderBody).unwrap()
      dispatch({
        type: 'updateOrderCurrencySuccess',
        payload: updatedOrder,
      })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      trackError('updateOrderCurrencyRTK', error)
      dispatch({
        type: 'orderRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const verifyCreditCardRTK = async () => {
    const cvcElement = elements?.getElement(CardCvcElement)
    try {
      if (!order?.number) {
        const errorMessage = t(
          `${TP}.noOrderNumberError2`,
          'No order available. Can not verify credit card.',
        )
        throw new Error(errorMessage)
      }
      if (!stripe || !cvcElement) {
        const errorMessage = t(
          `${TP}.noCvcError`,
          'Unable to verify your credit card. Stripe is unavailable',
        )
        throw new Error(errorMessage)
      }
      dispatch({ type: 'paymentMethodRequest' })
      // Create stripe token
      const { token, error } = await stripe.createToken('cvc_update', cvcElement)
      const stripeToken = token?.id
      if (error) {
        throw error
      }
      if (!stripeToken) {
        throw new Error(
          t(`${TP}.stripeTokenError`, {
            defaultValue: "No stripe token, can't verify credit card for order {orderNumber}",
            orderNumber: order.number,
          }),
        )
      }
      // Verify billing info on order with created stripe token
      const body = { cvcUpdateToken: stripeToken, orderNumber: order.number }
      const data = await verifyBilling(body).unwrap()
      sendTrackingCvvSubmit()
      dispatch({
        type: 'verifyCreditCardSuccess',
        payload: data?.success,
      })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      trackError('verifyCreditCardRTK', error)
      sendTrackingCvvSubmit(errorMessage)
      dispatch({
        type: 'paymentMethodRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  return {
    addNewCreditCardRTK,
    selectAffirmRTK,
    selectAfterPayRTK,
    selectCreditCardRTK,
    selectKlarnaRTK,
    selectPayPalRTK,
    updateOrderCurrencyRTK,
    verifyCreditCardRTK,
  }
}
